import { useState } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import Divider from '@mui/material/Divider'
import { useNavigate } from 'react-router-dom'
import { Menu as MenuIcon, LogOut, Settings } from 'lucide-react'
import { useAuth } from '../../core/auth/useAuth'
import { useI18n } from '../../core/i18n/useI18n'
import { useTopbarChrome } from './pageChrome'
import { AccountPreferences } from './AccountPreferences'
import { AssistenteButton } from '@/features/assistente/AssistenteButton'
import { useHubAtual } from '@/core/areas/useAreas'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const chrome = useTopbarChrome()
  const hub = useHubAtual()

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="default"
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        zIndex: (t) => t.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: 64, gap: 1 }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { md: 'none' }, color: 'text.secondary' }}
          aria-label={t('layout.topbar.openMenu')}
        >
          <MenuIcon size={20} />
        </IconButton>

        {/* Título da página (empurrado por useSetTopbarTitle) — libera a área útil do corpo. */}
        {chrome.title && (
          <Box sx={{ ml: 0.5, minWidth: 0, lineHeight: 1.1 }}>
            {chrome.eyebrow && (
              <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled' }}>
                {chrome.eyebrow}
              </Typography>
            )}
            <Typography noWrap sx={{ fontFamily: '"Fredoka", sans-serif', fontSize: '1.1875rem', fontWeight: 400, color: 'text.primary' }}>
              {chrome.title}
            </Typography>
          </Box>
        )}

        {/* Atalho da própria tela, logo depois do título (Samuel, 18/09/2026): na
            Frente de Trabalho é a sincronização com o Protheus, que antes só
            existia nas grades de Cadastros. */}
        {chrome.Action && (
          <Box sx={{ ml: 1.5, display: 'flex', alignItems: 'center', flex: 'none' }}>
            <chrome.Action />
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Imediatamente à esquerda do avatar; sem a permissão não existe, e na Farm
            fica escondido até o assistente aprender o hub (28/10/2026). */}
        {hub !== 'farm' && <AssistenteButton />}

        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ gap: 1, borderRadius: 2, px: 1, py: 0.5, minWidth: 0, flex: 'none' }}
          aria-label={t('layout.topbar.account')}
        >
          <Avatar sx={{ width: 34, height: 34, flex: 'none', bgcolor: 'primary.main', fontSize: '0.8125rem', fontWeight: 600 }}>
            {user ? initials(user.name) : '?'}
          </Avatar>
          {/* Nome longo não empurra a barra: recorta com reticências. */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', lineHeight: 1.1, minWidth: 0, maxWidth: 180 }}>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
              {user?.name ?? '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
              {/* Na Farm o subtítulo é o cargo, Gestão das Fazendas ou Diretoria Farm:
                  "Backoffice" é o vocabulário da transportadora e não diz nada à Jéssica. */}
              {hub === 'farm' && user?.cargo?.nome
                ? user.cargo.nome
                : user?.is_staff ? t('account.role.staff') : t('account.role.client')}
            </Typography>
          </Box>
        </IconButton>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{user?.name}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user?.email}</Typography>
          </Box>
          <Divider />
          <AccountPreferences />
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchor(null)
              navigate('/conta/configuracoes')
            }}
          >
            <ListItemIcon>
              <Settings size={16} />
            </ListItemIcon>
            {t('account.settings')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchor(null)
              logout()
            }}
          >
            <ListItemIcon>
              <LogOut size={16} />
            </ListItemIcon>
            {t('account.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}
